import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
    listFolders,
    setFolderPinned,
    FolderListResult,
    FolderSort,
    FolderListItem,
} from '../../services/api/folderService';
import AuthContext from '../../store/auth-context';
import PERMISSION_KEYS from '../../constants/permission-keys';
import LoadingRing from '../ui-components/LoadingRing';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { FolderCard } from './FolderCard';
import { FolderListTable } from './FolderListTable';
import { CreateEditFolderDialog } from './CreateEditFolderDialog';
import { SortSelect } from './SortSelect';
import { ViewModeToggle, ViewMode } from '../ui-components/ViewModeToggle';
import {
    getFolderPreferences,
    saveFolderPreferences,
} from '../../services/api/userPreferencesService';

const PAGE_SIZE = 9;

export const FoldersOverview = () => {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);
    const { toast } = useToast();
    const [data, setData] = useState<FolderListResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [pendingPinId, setPendingPinId] = useState<string | null>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [sort, setSort] = useState<FolderSort>('recent');
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const hydratedRef = useRef(false);

    const latestSortRef = useRef(sort);

    useEffect(() => {
        const hydrate = async () => {
            try {
                const prefs = await getFolderPreferences();
                if (prefs.sort) setSort(prefs.sort);
                if (prefs.view) setViewMode(prefs.view);
            } catch {
                // Silent
            } finally {
                hydratedRef.current = true;
            }
        };
        hydrate();
    }, []);

    useEffect(() => {
        if (!hydratedRef.current) return;
        const handle = setTimeout(() => {
            const save = async () => {
                try {
                    await saveFolderPreferences({ sort, view: viewMode });
                } catch {
                    // Silent
                }
            };
            save();
        }, 500);
        return () => clearTimeout(handle);
    }, [sort, viewMode]);

    useEffect(() => {
        latestSortRef.current = sort;
        // spinner only on first load, sorting shouldn't show a spinner
        const isInitial = !data;
        let cancelled = false;
        const load = async () => {
            if (isInitial) setLoading(true);
            try {
                const result = await listFolders({
                    sort,
                    limit: PAGE_SIZE,
                    skip: 0,
                });
                if (!cancelled) setData(result);
            } catch {
                if (!cancelled) {
                    toast({
                        title: t('folders.common.error'),
                        description: t('folders.overview.toasts.loadFailed'),
                        variant: 'destructive',
                    });
                }
            } finally {
                if (!cancelled && isInitial) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [sort]);

    const handleViewMore = async () => {
        if (!data || loadingMore) return;
        const requestedSort = sort;
        setLoadingMore(true);
        try {
            const result = await listFolders({
                sort: requestedSort,
                limit: PAGE_SIZE,
                skip: data.unpinned.length,
            });
            if (requestedSort !== latestSortRef.current) return;
            setData((prev) =>
                prev
                    ? {
                          // Pinned comes back in full on every response;
                          // unpinned gets appended for the next page.
                          pinned: result.pinned,
                          unpinned: [...prev.unpinned, ...result.unpinned],
                          hasMore: result.hasMore,
                      }
                    : result
            );
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.overview.toasts.loadMoreFailed'),
                variant: 'destructive',
            });
        } finally {
            setLoadingMore(false);
        }
    };

    const handleTogglePin = async (folderId: string, nextIsPinned: boolean) => {
        setPendingPinId(folderId);
        try {
            await setFolderPinned(folderId, nextIsPinned);
            const limit = data?.unpinned.length || PAGE_SIZE;
            const result = await listFolders({ sort, limit, skip: 0 });
            setData(result);
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.overview.toasts.pinFailed'),
                variant: 'destructive',
            });
        } finally {
            setPendingPinId(null);
        }
    };

    const canManage = ctx.hasPermission(PERMISSION_KEYS.manageGroups);
    const hasPinned = data ? data.pinned.length > 0 : false;
    const hasUnpinned = data ? data.unpinned.length > 0 : false;
    const hasFolders = hasPinned || hasUnpinned;

    if (!canManage && !hasFolders) {
        return null;
    }

    const renderFolders = (folders: FolderListItem[]) => {
        if (viewMode === 'grid') {
            return (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {folders.map((folder) => (
                        <FolderCard
                            key={folder._id}
                            folder={folder}
                            onTogglePin={
                                canManage ? handleTogglePin : undefined
                            }
                            pinDisabled={pendingPinId === folder._id}
                        />
                    ))}
                </div>
            );
        }
        return (
            <FolderListTable
                folders={folders}
                onTogglePin={canManage ? handleTogglePin : undefined}
                pendingPinId={pendingPinId}
            />
        );
    };

    const newFolderButton = canManage ? (
        <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus aria-hidden="true" />
            {t('folders.overview.newFolder')}
        </Button>
    ) : null;

    return (
        <section className="w-full max-w-4xl px-4 pb-32 sm:px-6">
            {loading ? (
                <div className="flex justify-center py-8">
                    <LoadingRing size="large" />
                </div>
            ) : data && hasFolders ? (
                <>
                    {hasPinned && (
                        <section className="mt-4">
                            <div className="mb-3 flex items-center justify-between">
                                <h3 className="text-base font-medium text-foreground">
                                    {t('folders.overview.sections.pinned')}
                                </h3>
                                <div className="flex items-center gap-2">
                                    <ViewModeToggle
                                        value={viewMode}
                                        onChange={setViewMode}
                                    />
                                    {newFolderButton}
                                </div>
                            </div>
                            {renderFolders(data.pinned)}
                        </section>
                    )}
                    {hasUnpinned && (
                        <section className={hasPinned ? 'mt-8' : 'mt-4'}>
                            <div className="mb-3 flex items-center justify-between">
                                <h3 className="text-base font-medium text-foreground">
                                    {t('folders.overview.sections.all')}
                                </h3>
                                <div className="flex items-center gap-2">
                                    <SortSelect
                                        value={sort}
                                        onChange={setSort}
                                    />
                                    {!hasPinned && (
                                        <>
                                            <ViewModeToggle
                                                value={viewMode}
                                                onChange={setViewMode}
                                            />
                                            {newFolderButton}
                                        </>
                                    )}
                                </div>
                            </div>
                            {renderFolders(data.unpinned)}
                        </section>
                    )}
                    {data.hasMore && (
                        <div className="mt-6 flex justify-center">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleViewMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? (
                                    <LoadingRing size="small" />
                                ) : (
                                    t('folders.overview.viewMore')
                                )}
                            </Button>
                        </div>
                    )}
                </>
            ) : (
                <section className="mt-4">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-base font-medium text-foreground">
                            {t('folders.overview.sections.all')}
                        </h3>
                        {newFolderButton}
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {t('folders.overview.emptyState')}
                    </p>
                </section>
            )}
            <CreateEditFolderDialog
                mode="create"
                open={createDialogOpen}
                onOpenChange={setCreateDialogOpen}
            />
        </section>
    );
};
